// ============================================================
// UNSTABLE PIERRE — APP ENTRY POINT
// The standalone game's root. Boots into W'ily's Pier.
//
// Identity model (its own game, shared IE Firebase project):
//   1. If we arrive with ?uid=UID in the URL, another IE game handed
//      this player off to us. Adopt that uid, strip the param from the
//      URL (history.replaceState), and skip our own login.
//   2. Otherwise, show an email + password auth screen (sign up / log in).
//      Firebase Auth gives us a stable uid tied to the account, so a
//      player's hall persists across devices and browsers.
//   3. Load profiles/{uid}. If none exists yet, create a minimal one so
//      nothing downstream ever sees a null profile.
//   4. If the profile has no display name, ask for one once.
//   5. Render <UnstablePierre/>.
//
// This is NOT the Terror app. Terror, Snake Eyes, and Pierre are each
// their own game with their own entry point, all sharing the terror-tcg
// Firebase project and the ?uid= handoff convention.
// ============================================================

import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { ref, get, set, runTransaction } from "firebase/database";
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { db, auth } from "./firebase";
import { listenToProfile } from "./progression";
import { SD } from "./theme";
import UnstablePierre from "./UnstablePierre";
import { setAemiliaHidden } from "./AemiliaGuide";
import { setBackdropHidden } from "./ieBackdrop";

// ─────────────────────────────────────────────────────────────
// Read (and strip) the ?uid= handoff param.
// ─────────────────────────────────────────────────────────────
function consumeUidParam() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const uid = params.get("uid");
    if (uid) {
      params.delete("uid");
      const qs = params.toString();
      const newUrl =
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
      return uid;
    }
  } catch {
    /* no-op */
  }
  return null;
}

// Ensure a minimal profile exists so downstream never sees null.
async function ensureProfile(uid, name) {
  const fresh = {
    name: name || "",
    gender: "f",
    companionId: "default",
    divineCrests: 0,
    createdAt: Date.now(),
  };

  // ⚠ THE READ COMES FIRST, AND IT IS NOT DECORATION. This is the
  // largest read the client makes — `profiles/{uid}` carries gallery,
  // achievements, minerium and unlockedCosmetics — and for a returning
  // player the answer is always "it exists". Going straight to a
  // transaction made every boot download that subtree, run an updater,
  // abort, and download it AGAIN. Read first, and the common case costs
  // exactly what it used to.
  try {
    const snap = await get(ref(db, `profiles/${uid}`));
    if (snap.exists()) return { ...snap.val(), uid };
  } catch { /* fall through and let the transaction decide */ }

  // ⚠⚠ AND THE CREATE IS A TRANSACTION, because a read-then-whole-object
  // `set()` on this node is the exact shape that caused the April 2026
  // holo-card + crest wipe (PIERRE_PRIME L9). The read above is a fast
  // path, NOT the guard — the guard is the abort below, where the check
  // and the write are one operation instead of two.
  // ⚠ `applyLocally: false`: an RTDB transaction runs optimistically
  // against `null` first, so with the default every local listener would
  // momentarily see `fresh` — zero Crests, no gallery — and paint it,
  // before the server aborted and rolled it back.
  try {
    const tx = await runTransaction(
      ref(db, `profiles/${uid}`),
      (cur) => (cur ? undefined : fresh),      // abort — a profile is already there
      { applyLocally: false }
    );
    if (!tx.committed) {
      const snap = await get(ref(db, `profiles/${uid}`));
      return { ...(snap.val() || {}), uid };
    }
    return { ...fresh, uid };
  } catch {
    // ⚠ A REFUSED WRITE MUST NOT HANG THE BOOT. When OW-9 locks this
    // node down, a rejected transaction here would reject out of the
    // auth effect, no profile listener would ever attach, and every
    // existing account would sit on the loading screen forever.
    const snap = await get(ref(db, `profiles/${uid}`)).catch(() => null);
    // ⚠ `null`, NOT a zeroed profile. If both the read and the re-read
    // failed we do not know whether this account has a gallery, a
    // Minerium and a pile of Crests — and handing back `fresh` would be
    // a plausible-looking empty one. The caller discards this value
    // today (state comes from `listenToProfile`), so `null` costs
    // nothing; inventing an empty account would cost everything the
    // first time somebody DID read it.
    return snap?.exists() ? { ...snap.val(), uid } : null;
  }
}

// Turn Firebase auth error codes into friendly lines.
function friendlyAuthError(code) {
  switch (code) {
    case "auth/invalid-email":          return "That email doesn't look right.";
    case "auth/missing-password":       return "Enter a password.";
    case "auth/weak-password":          return "Password must be at least 6 characters.";
    case "auth/email-already-in-use":   return "That email already has an account — try logging in.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":         return "Email or password is incorrect.";
    case "auth/too-many-requests":      return "Too many tries. Wait a moment and retry.";
    case "auth/network-request-failed": return "Network hiccup. Check your connection.";
    default:                            return "Something went wrong. Try again.";
  }
}

// ─────────────────────────────────────────────────────────────
// BOOT SCREEN — while auth + profile resolve.
// ─────────────────────────────────────────────────────────────
function BootScreen({ message = "Anchoring to the pier…" }) {
  return (
    <div style={screenWrap}>
      <div style={{ textAlign: "center" }}>
        <div style={pierTitle}>W'ILY'S PIER</div>
        <div style={{
          fontSize: 11, letterSpacing: 4, color: SD.starDim,
          fontFamily: SD.fontDisplay, marginTop: 10,
        }}>{message}</div>
        <div style={{
          width: 160, height: 3, margin: "24px auto 0",
          background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden",
        }}>
          <div style={{
            width: "40%", height: "100%",
            background: `linear-gradient(90deg, transparent, ${SD.gold}, transparent)`,
            animation: "pierBoot 1.2s ease-in-out infinite",
          }} />
        </div>
      </div>
      <style>{`@keyframes pierBoot { 0%{transform:translateX(-120px)} 100%{transform:translateX(280px)} }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// AUTH SCREEN — email + password, with sign-up / log-in toggle.
// ─────────────────────────────────────────────────────────────
function AuthScreen() {
  const [mode, setMode]   = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [pass, setPass]   = useState("");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const emailRef = useRef(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const submit = async () => {
    if (busy) return;
    setError("");
    const e = email.trim();
    if (!e) { setError("Enter your email."); return; }
    if (!pass) { setError("Enter a password."); return; }
    setBusy(true);
    try {
      if (mode === "signup") {
        await createUserWithEmailAndPassword(auth, e, pass);
      } else {
        await signInWithEmailAndPassword(auth, e, pass);
      }
      // onAuthStateChanged in App takes over from here.
    } catch (err) {
      setError(friendlyAuthError(err?.code));
      setBusy(false);
    }
  };

  return (
    <div style={screenWrap}>
      <div style={authCard}>
        <div style={pierTitle}>W'ILY'S PIER</div>
        <div style={{
          fontSize: 11, color: SD.starDim, fontFamily: SD.fontDisplay,
          letterSpacing: 2, marginTop: 8, marginBottom: 22,
        }}>
          {mode === "signup" ? "Claim your place on the pier." : "Welcome back to the pier."}
        </div>

        <input
          ref={emailRef}
          type="email"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          onKeyDown={(ev) => { if (ev.key === "Enter") submit(); }}
          placeholder="Email"
          autoComplete="email"
          style={inputStyle}
        />
        <input
          type="password"
          value={pass}
          onChange={(ev) => setPass(ev.target.value)}
          onKeyDown={(ev) => { if (ev.key === "Enter") submit(); }}
          placeholder="Password"
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          style={{ ...inputStyle, marginTop: 10 }}
        />

        {error && (
          <div style={{
            fontSize: 11, color: "#ff6b8a", fontFamily: SD.fontDisplay,
            marginTop: 12, letterSpacing: 0.5,
          }}>{error}</div>
        )}

        <button onClick={submit} disabled={busy} style={{
          width: "100%", marginTop: 18, padding: "13px",
          background: busy
            ? "rgba(255,255,255,0.06)"
            : `linear-gradient(135deg, ${SD.gold}33, ${SD.gold}55)`,
          border: `1px solid ${busy ? SD.void4 : SD.gold}`, borderRadius: 8,
          color: busy ? SD.starDim : SD.goldBright,
          fontSize: 13, fontWeight: 900, fontFamily: SD.fontDisplay,
          letterSpacing: 3, cursor: busy ? "default" : "pointer",
          boxShadow: busy ? "none" : `0 0 20px ${SD.gold}33`,
        }}>
          {busy ? "…" : (mode === "signup" ? "◆ CREATE ACCOUNT" : "◆ ENTER THE PIER")}
        </button>

        <div style={{
          marginTop: 18, fontSize: 11, color: SD.starDim, fontFamily: SD.fontDisplay,
        }}>
          {mode === "signup" ? "Already have an account?" : "New to the pier?"}{" "}
          <span
            onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setError(""); }}
            style={{ color: SD.goldBright, cursor: "pointer", letterSpacing: 1 }}
          >
            {mode === "signup" ? "Log in" : "Sign up"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// NAME SCREEN — first-run display name (only when profile has none).
// ─────────────────────────────────────────────────────────────
function NameScreen({ onSubmit }) {
  const [name, setName] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length < 1) return;
    onSubmit(trimmed.slice(0, 20));
  };

  return (
    <div style={screenWrap}>
      <div style={authCard}>
        <div style={pierTitle}>W'ILY'S PIER</div>
        <div style={{
          fontSize: 11, color: SD.starDim, fontFamily: SD.fontDisplay,
          letterSpacing: 2, marginTop: 8, marginBottom: 24,
        }}>What should the pier call you?</div>

        <input
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="Your name"
          maxLength={20}
          style={{ ...inputStyle, textAlign: "center" }}
        />
        <button onClick={submit} style={{
          width: "100%", marginTop: 18, padding: "13px",
          background: `linear-gradient(135deg, ${SD.gold}33, ${SD.gold}55)`,
          border: `1px solid ${SD.gold}`, borderRadius: 8,
          color: SD.goldBright, fontSize: 13, fontWeight: 900,
          fontFamily: SD.fontDisplay, letterSpacing: 3, cursor: "pointer",
          boxShadow: `0 0 20px ${SD.gold}33`,
        }}>◆ ENTER THE PIER</button>
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────
const screenWrap = {
  position: "fixed", inset: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
  background: "#020010", fontFamily: SD.fontDisplay, padding: 16,
};
const authCard = {
  width: "min(400px, 92vw)", textAlign: "center",
  background: "rgba(5,3,20,0.9)",
  border: `1px solid ${SD.gold}55`, borderRadius: 16,
  padding: "34px 30px", boxShadow: `0 0 40px ${SD.gold}22`,
};
const pierTitle = {
  fontSize: 28, letterSpacing: 5, color: SD.goldBright,
  fontFamily: SD.fontDisplay, fontWeight: 900,
  textShadow: `0 0 18px ${SD.gold}`,
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: "rgba(255,255,255,0.05)",
  border: `1px solid ${SD.void4}`, borderRadius: 8,
  padding: "12px 14px", color: SD.starWhite,
  fontSize: 15, fontFamily: SD.fontDisplay, letterSpacing: 1,
  outline: "none",
};

// ─────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────
export default function App() {
  const [uid, setUid]             = useState(null);
  const [profile, setProfile]     = useState(null);
  const [authKnown, setAuthKnown] = useState(false); // has auth resolved once?
  const [phase, setPhase]         = useState("boot"); // boot | auth | name | ready
  const handoffUid = useRef(null);

  // Capture the handoff uid once, before auth resolves.
  useEffect(() => {
    handoffUid.current = consumeUidParam();
    if (handoffUid.current) setUid(handoffUid.current); // trust arriving IE game
  }, []);

  // ── Auth state ────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthKnown(true);
      if (handoffUid.current) return;   // handoff uid always wins
      setUid(user?.uid || null);
    });
    return () => unsub();
  }, []);

  // Once auth is known and there's no uid, show the auth screen.
  useEffect(() => {
    if (!authKnown && !handoffUid.current) return;
    if (!uid) setPhase("auth");
  }, [authKnown, uid]);

  // ── Profile: ensure it exists, then subscribe live ────────
  useEffect(() => {
    if (!uid) return;
    let unsub = null;
    let cancelled = false;

    (async () => {
      await ensureProfile(uid);
      if (cancelled) return;
      unsub = listenToProfile(uid, (p) => {
        if (cancelled) return;
        if (p) {
          setProfile(p);
          setPhase(p.name && p.name.trim() ? "ready" : "name");
        }
      });
    })();

    return () => { cancelled = true; if (unsub) unsub(); };
  }, [uid]);

  // ── Name submit → write name, go live ─────────────────────
  const handleName = async (name) => {
    if (!uid) return;
    await set(ref(db, `profiles/${uid}/name`), name);
    setPhase("ready");
  };

  // ── Logout → sign out, back to the auth screen ────────────
  const handleLogout = async () => {
    handoffUid.current = null;
    setProfile(null);
    setUid(null);
    setPhase("auth");
    try { await signOut(auth); } catch { /* no-op */ }
  };

  // Aemilia waits until you're on the pier: the login and name screens have no
  // Friends pill or doors for her answers to point at (review, 2026-10-20).
  const onPier = phase === "ready" && !!uid && !!profile;
  useLayoutEffect(() => { setAemiliaHidden(!onPier, "login"); setBackdropHidden(!onPier, "login"); }, [onPier]);

  // ── Render by phase ───────────────────────────────────────
  if (phase === "auth") return <AuthScreen />;
  if (phase === "boot" || !uid || !profile) return <BootScreen />;
  if (phase === "name") return <NameScreen onSubmit={handleName} />;

  return (
    <UnstablePierre
      playerUid={uid}
      playerName={profile.name}
      profile={profile}
      onLogout={handleLogout}
    />
  );
}
